import { useState, useMemo, useCallback, useRef, useEffect } from "react"
import { Info, TriangleAlert, Building2 } from "lucide-react"
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip"
import { Input } from "@/components/ui/input"
import { CommuneInfoPopover } from "./commune-info-popover"
import type { CommuneData } from "./commune-info-popover"
import { cn } from "@/lib/utils"

interface CommuneInfos {
  code_com: string
  code_dep: string
  nom: string
}

interface CommuneSelectorProps {
  onSelectCommune: (code: string) => Promise<void>
  isCommuneCovered?: boolean
  communes?: CommuneInfos[]
  selectedCommune?: { code?: string; codeDepartement?: string; libelle?: string } | null
}

const normalizeText = (text: string): string => {
  return text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
}

export const CommuneSelector = ({
  onSelectCommune,
  isCommuneCovered = true,
  communes = [],
  selectedCommune = null,
}: CommuneSelectorProps) => {
  const [communeInfoOpen, setCommuneInfoOpen] = useState(false)
  const [inputValue, setInputValue] = useState("")
  const [isOpen, setIsOpen] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const containerRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLUListElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Pre-normalize commune names once when communes change
  const normalizedCommunes = useMemo(
    () =>
      [...communes]
        .sort((a, b) => a.nom.localeCompare(b.nom))
        .map((c) => ({ commune: c, normalized: normalizeText(c.nom) })),
    [communes],
  )

  const filteredCommunes = useMemo(() => {
    const normalized = normalizeText(inputValue)
    if (!normalized) return []
    return normalizedCommunes
      .filter((o) => o.normalized.includes(normalized))
      .sort((a, b) => {
        const aStarts = a.normalized.startsWith(normalized) ? 0 : 1
        const bStarts = b.normalized.startsWith(normalized) ? 0 : 1
        if (aStarts !== bStarts) return aStarts - bStarts
        return a.normalized.localeCompare(b.normalized)
      })
      .slice(0, 50)
      .map((o) => o.commune)
  }, [normalizedCommunes, inputValue])

  const handleSelect = useCallback(
    (commune: CommuneInfos) => {
      onSelectCommune(commune.code_com)
      setInputValue(`${commune.nom} (${commune.code_dep})`)
      setIsOpen(false)
      setHighlightedIndex(-1)
    },
    [onSelectCommune],
  )

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value)
    setIsOpen(true)
    setHighlightedIndex(0)
  }, [])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isOpen || filteredCommunes.length === 0) {
        if (e.key === "ArrowDown" && inputValue.trim()) {
          setIsOpen(true)
        }
        return
      }

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault()
          setHighlightedIndex((i) => Math.min(i + 1, filteredCommunes.length - 1))
          break
        case "ArrowUp":
          e.preventDefault()
          setHighlightedIndex((i) => Math.max(i - 1, 0))
          break
        case "Enter":
          e.preventDefault()
          if (highlightedIndex >= 0 && highlightedIndex < filteredCommunes.length) {
            handleSelect(filteredCommunes[highlightedIndex])
          }
          break
        case "Escape":
          setIsOpen(false)
          setHighlightedIndex(-1)
          break
      }
    },
    [isOpen, filteredCommunes, highlightedIndex, handleSelect, inputValue],
  )

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const item = listRef.current.children[highlightedIndex] as HTMLElement | undefined
      item?.scrollIntoView({ block: "nearest" })
    }
  }, [highlightedIndex])

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  // Display selected commune label when selectedCommune changes externally
  useEffect(() => {
    if (selectedCommune?.libelle) {
      setInputValue(`${selectedCommune.libelle} (${selectedCommune.codeDepartement || ""})`)
    }
  }, [selectedCommune?.code])

  const showDropdown = isOpen && filteredCommunes.length > 0

  return (
    <>
      <div data-tour-step="commune-selector" className="flex items-center p-2">
        <span className="flex items-center justify-center rounded-lg text-primary bg-primary/[0.08] flex-shrink-0 w-8 h-8">
          <Building2 className="w-5 h-5" />
        </span>
        <div className="flex-1 min-w-0 relative" ref={containerRef}>
          <Input
            ref={inputRef}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={(e) => {
              e.target.select()
              if (inputValue.trim()) setIsOpen(true)
            }}
            placeholder="Choisir une commune..."
            spellCheck={false}
            autoComplete="off"
            data-commune-search
            className={cn(
              "w-full border-none shadow-none focus-visible:ring-0 focus-visible:border-none",
              selectedCommune ? "text-base font-semibold text-blue-800" : "",
            )}
          />
          {showDropdown && (
            <ul
              ref={listRef}
              role="listbox"
              className="absolute left-0 top-full z-50 mt-1.5 max-h-72 w-full min-w-[calc(100%+1.75rem)] overflow-y-auto rounded-lg bg-popover p-1 text-popover-foreground shadow-md ring-1 ring-foreground/10"
            >
              {filteredCommunes.map((item, index) => (
                <li
                  key={item.code_com}
                  role="option"
                  aria-selected={highlightedIndex === index}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  onMouseDown={(e) => {
                    e.preventDefault() // prevent input blur
                    handleSelect(item)
                  }}
                  className={cn(
                    "relative flex w-full cursor-pointer items-center rounded-md py-1 pl-1.5 pr-8 text-sm select-none",
                    highlightedIndex === index ? "bg-accent text-accent-foreground" : "",
                  )}
                >
                  {item.nom} ({item.code_dep})
                </li>
              ))}
            </ul>
          )}
        </div>
        {!isCommuneCovered && selectedCommune && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="w-6 h-6 flex items-center justify-center flex-shrink-0 rounded-md bg-amber-100 text-amber-600">
                  <TriangleAlert className="w-3.5 h-3.5" />
                </span>
              </TooltipTrigger>
              <TooltipContent>Commune non couverte par votre contrat</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        <button
          onClick={() => setCommuneInfoOpen(!communeInfoOpen)}
          disabled={!selectedCommune}
          className={cn(
            "flex-shrink-0 flex items-center justify-center rounded-lg transition-all duration-150 w-7 h-7",
            !selectedCommune
              ? "text-gray-300 cursor-not-allowed"
              : communeInfoOpen
                ? "text-primary bg-primary/[0.08] cursor-pointer"
                : "text-gray-400 hover:text-gray-600 hover:bg-gray-100 cursor-pointer",
          )}
          title="Details de la commune"
        >
          <Info className="w-3.5 h-3.5" />
        </button>
      </div>

      <CommuneInfoPopover
        open={communeInfoOpen}
        onClose={() => setCommuneInfoOpen(false)}
        selectedCommune={selectedCommune as unknown as CommuneData}
      />
    </>
  )
}
